// Navegação vertical ENTRE trilhos (↑/↓): decisão de COLUNA como função pura.
//
// A row desliga a navegação geométrica do Norigin (onArrowPress → false) e
// resolve o alvo na mão, pelo DOM. O CRITÉRIO dessa resolução — "qual card da
// row vizinha está na mesma coluna X do card focado" — vive aqui, isolado do
// DOM, pra ser testável sem layout e reusável.

export interface RowNavCardRect {
  key: string
  /** Borda esquerda do card em coordenadas de viewport (getBoundingClientRect.left). */
  left: number
  /** Borda direita do card (getBoundingClientRect.right). */
  right: number
}

export interface RowNavViewport {
  left: number
  right: number
}

/**
 * Escolhe, entre os cards da row vizinha, o de centro horizontal mais próximo
 * do `desiredCenter` (a COLUNA que o usuário quer) — preservando a coluna ao
 * subir / descer entre trilhos.
 *
 * Só considera cards VISÍVEIS dentro do `viewport` horizontal da row vizinha
 * (com folga de 4px), pra nunca focar um card rolado pra fora da tela / fora da
 * janela virtual. Sem nenhum card visível medível, cai no primeiro card.
 *
 * ANTES a row devolvia o PRIMEIRO card visível (o mais à esquerda), ignorando a
 * coluna — então ↑/↓ sempre caía no início da row vizinha. Esta função é o
 * conserto: mesmo critério geométrico do Norigin, mas explícito e testável.
 */
export function pickAlignedCardKey(
  desiredCenter: number,
  cards: readonly RowNavCardRect[],
  viewport: RowNavViewport,
): string | null {
  let bestKey: string | null = null
  let bestDist = Number.POSITIVE_INFINITY
  for (const c of cards) {
    // Fora da viewport horizontal da row vizinha → ignora (não focar off-screen).
    if (c.right <= viewport.left + 4 || c.left >= viewport.right - 4) continue
    const center = (c.left + c.right) / 2
    const dist = Math.abs(center - desiredCenter)
    if (dist < bestDist) {
      bestDist = dist
      bestKey = c.key
    }
  }
  return bestKey ?? cards[0]?.key ?? null
}

// ===== COLUNA DESEJADA (memória de coluna entre trilhos) =====
//
// POR QUE EXISTE
// --------------
// `pickAlignedCardKey` só consegue escolher entre os cards que EXISTEM na row
// vizinha. Quando o trilho de baixo é mais CURTO que a coluna do focado (Home
// real: "Continuar assistindo" / "Favoritos" / rail curado com 2-3 itens), o
// ↓ GRUDA no último card dele — e, sem memória, essa coluna encolhida vira a
// nova coluna pro resto da descida: o trilho seguinte, mesmo cheio, recebe o
// foco na coluna 1 em vez da 3.
//
// Foi exatamente o reporte do board (2.22.0): ↓ do trilho 1 coluna 3 (left 831)
// pousava no trilho 2 em left 295, e o ↑ depois "preservava" 295 → 295. A
// assimetria era ILUSÓRIA: os dois sentidos sempre usaram o mesmo
// `pickAlignedCardKey`. O que faltava era lembrar a coluna PRETENDIDA — o ↑
// parecia certo só porque subia de uma coluna já encolhida (295 é coluna
// válida na row de cima) pra uma row larga.
//
// COMO FUNCIONA — VALIDAÇÃO NA LEITURA, NÃO NOTIFICAÇÃO NO POUSO
// --------------------------------------------------------------
// A coluna desejada é o centro X que o usuário escolheu com ←/→. Ela SOBREVIVE
// às transições ↑/↓ (inclusive as que grudam num trilho curto) e morre assim
// que o foco vai parar em qualquer lugar que não seja o alvo daquele movimento
// vertical.
//
// A memória guarda, junto com o centro, a focusKey que ELA esperava receber. Na
// LEITURA, quem pergunta diz quem está focado agora: se não for a key esperada,
// o foco passou por outro lugar no meio e a coluna é descartada.
//
// Por que assim e não "cada focável avisa que ganhou foco": avisar exigiria um
// call site em TODO focável do app — `RowCard`, os botões ‹ ›, TopNav, chips
// (`cat-card-*`/`rail-cat-*`) e os cards de GRID (MovieDrillView), que são
// `Focusable` puro. Qualquer um esquecido vira buraco silencioso, e o buraco é
// real: o drill de categoria (MoviesScreen/SeriesScreen) entra e sai do grid
// inteiro SEM trocar de rota, então nem a blindagem por pathname o cobriria.
// Validar na leitura fecha a CLASSE com um único ponto de checagem — o mesmo
// lugar (e o único) que consome a memória —, sem tocar o `Focusable`
// compartilhado e sem depender de ninguém lembrar de avisar.
//
// Estado de MÓDULO de propósito: trilhos irmãos não se conhecem (mesmo motivo
// do verticalRowMount). É um número + uma key, zerado na invalidação — não
// cresce, não registra listener.

let desiredCenter: number | null = null
let expectedKey: string | null = null
/** Onde o card K estava na TELA quando a memória nasceu. É a âncora do
 *  invariante — ver `getDesiredColumn`. */
let anchorCenter: number | null = null

/**
 * Registra a coluna que o usuário quer manter (`center`), QUAL focusKey deve
 * recebê-la (`targetKey`) e ONDE esse card está na tela agora (`targetCenter`).
 * Chamado ANTES do `setFocus` do movimento vertical.
 */
export function rememberDesiredColumn(
  center: number,
  targetKey: string,
  targetCenter: number,
): void {
  desiredCenter = center
  expectedKey = targetKey
  anchorCenter = targetCenter
}

/**
 * A coluna desejada, se ela ainda VALE.
 *
 * O INVARIANTE REAL é: `desiredCenter` continua válido enquanto o card que o
 * movimento vertical mirou não se mover na horizontal. As três versões
 * anteriores deste módulo validaram PROXIES desse invariante — quem pousou
 * (push via `noteFocusLanded`), quem pergunta (só a key), qual tecla foi
 * apertada (`forgetDesiredColumn` no ←/→) — e cada proxy furou por um caminho
 * diferente. Aqui checamos o invariante DIRETO: a key tem que bater E o card
 * tem que estar onde estava.
 *
 * Fecha de uma vez os movimentos que nenhum proxy pegava:
 *  - `scrollByPage` dos botões ‹ ›, que rola o trilho uma página inteira sem
 *    ←/→ nenhum (no build web, com mouse: clica em ‹ ›, clica de volta no card,
 *    recebia a coluna obsoleta);
 *  - entrar/sair do drill de categoria com o trilho já rolado — o remount zera
 *    o `scrollLeft` e o card volta pra posição natural;
 *  - re-sort do trilho mantendo a focusKey (as keys vêm do conteúdo: `pos-{id}`,
 *    `fav-{id}`), então um refetch que reordena "Continuar assistindo" muda o x
 *    do card SEM tecla e SEM troca de foco — nem o modelo push via.
 *
 * `tolerance` = meio passo do carrossel (ver o call site em ScrollableRow):
 * é exatamente onde a escolha do `pickAlignedCardKey` VIRA. Ele vence pelo
 * centro mais próximo e os cards distam `pitch`; um desvio menor que `pitch/2`
 * ainda elege a mesma coluna, e a partir daí elege a vizinha. Abaixo do limiar
 * a memória não muda o resultado; acima, muda — então é o ponto certo de corte.
 */
export function getDesiredColumn(
  currentFocusKey: string,
  currentCenter: number,
  tolerance: number,
): number | null {
  if (expectedKey !== currentFocusKey) {
    forgetDesiredColumn()
    return null
  }
  if (anchorCenter === null || Math.abs(currentCenter - anchorCenter) > tolerance) {
    forgetDesiredColumn()
    return null
  }
  return desiredCenter
}

/**
 * Esquece a coluna pretendida. Chamado no ←/→ do `RowCard`.
 *
 * POR QUE CONTINUA EXISTINDO depois da checagem geométrica: ele não é
 * redundante, cobre um caso que a geometria NÃO alcança — mudança de INTENÇÃO
 * sem mudança de posição.
 *
 * O caso: o ↓ caiu num trilho CURTO e grudou no último card (clamp), então a
 * coluna pretendida (ex: 836) é diferente da posição do card focado (ex: 300).
 * O usuário anda com ←/→ entre cards já visíveis e volta pro mesmo card — o
 * trilho não rolou, então o card está exatamente onde estava e a geometria
 * aprova. Mas o usuário mexeu na horizontal de propósito: a coluna que ele quer
 * agora é a do card onde ele está (300), não a herdada (836). Sem este ponto o
 * ↓ seguinte pularia pra coluna 3 logo depois de um movimento horizontal
 * explícito — exatamente o tipo de salto que o reporte original pedia pra
 * matar.
 *
 * Ou seja: a geometria valida "o card não se moveu"; isto valida "o usuário não
 * escolheu outra coluna". São invariantes diferentes e ambos precisam valer.
 *
 * NÃO mexe em `lastPathname`: essa é a âncora da blindagem por rota e limpá-la
 * aqui faria a próxima troca de tela comparar contra `null`.
 */
export function forgetDesiredColumn(): void {
  desiredCenter = null
  expectedKey = null
  anchorCenter = null
}

/** Reset TOTAL, incluindo a âncora de rota. Só pra teste (isolamento entre
 *  casos) — no app use `forgetDesiredColumn` ou a blindagem por pathname. */
export function resetDesiredColumn(): void {
  forgetDesiredColumn()
  lastPathname = null
}

let lastPathname: string | null = null

/**
 * BLINDAGEM: zera a memória ao TROCAR DE TELA.
 *
 * A validação na leitura (`getDesiredColumn`) já cobre o caso real: quem
 * pergunta tem que ser o card que o ↑/↓ mirou. Mas ela depende de um
 * invariante IMPLÍCITO — que nenhuma tela nova tenha um card com a MESMA
 * focusKey que a tela anterior esperava. Hoje isso vale pela convenção de
 * prefixo (`hm-`, `fav-`, `pos-`, `recent-`, `${cacheKey}-`), só que convenção
 * não é tipo, e ninguém quebra convenção de propósito.
 *
 * Ancorar no PATHNAME (e não no unmount de um componente) é o que cobre todas
 * as saídas: rotas são planas em routes.tsx (não há layout que envolva as
 * telas) e as telas são lazy — com Suspense a ordem de unmount/mount entre a
 * tela velha e a nova não é determinística, então pendurar o reset no unmount
 * de um wrapper deixaria buracos. Mudou o pathname, mudou a tela: os trilhos
 * de onde a coluna foi escolhida não existem mais.
 *
 * Devolve `true` quando de fato zerou (útil pra teste).
 */
export function resetDesiredColumnOnPathChange(pathname: string): boolean {
  if (lastPathname === pathname) return false
  lastPathname = pathname
  forgetDesiredColumn()
  return true
}
